import React, { ChangeEvent, memo, useCallback, useRef } from "react";
import colors from "app/colors";
import { PaperclipIcon } from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { useFile } from "modules/providers/file-provider";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";

interface IProps {
  draftId: string;
}

const ComposeBottomToolbarFileButton = ({ draftId }: IProps) => {
  const { draft, isDraftLoaded, bottomButtonTypes } = useMessageThreadDraft({
    draftId,
  });

  const { uploadFiles } = useFile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;

      if (!fileList) {
        return;
      }

      const files = Array.from(fileList);
      uploadFiles(draftId, files);

      // Allow the user to delete then re-add the same file, or add the same file multiple times if they choose to
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [draftId, uploadFiles],
  );

  if (!draft || !isDraftLoaded) return null;

  if (!bottomButtonTypes.includes("file")) {
    return null;
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={onFileInputChange}
      />
      <CircularIconButton
        title="Add file"
        size="xs"
        variant="ghost"
        color={colors.gray40}
        icon={<PaperclipIcon fill={colors.gray40} boxSize={5} />}
        onClick={onClick}
        tabIndex={-1}
        aria-label="Add file"
      />
    </>
  );
};

export default memo(ComposeBottomToolbarFileButton);
